import { MutationFunction } from "@apollo/client";
import backIcon from "@assets/images/back.svg";
import printIcon from "@assets/images/print.svg";
import switchHorizontalIcon from "@assets/images/swap-horizontal.svg";
import { Button, Paper } from "@material-ui/core";
import { useUserPermissions } from "@saleor/auth/hooks/useUserPermissions";
import { changeOrderPath } from "@saleor/changeOrderDomestic/urls";
import DecimalFormat from "@saleor/components/DecimalFormat";
import MyTypography from "@saleor/components/MyTypography";
import { PreviewOrderQueryParam } from "@saleor/createOrder/urls";
import { getPreviewOrderFilterTable } from "@saleor/createOrder/views/sort";
import {
  DownloadPdfOrderMutation,
  DownloadPdfOrderMutationVariables,
  PermissionEnum,
  PreviewCustomerOrderQuery,
  PreviewDomesticPageOrderQuery,
  PrintChangeOrderMutation,
  PrintChangeOrderMutationVariables
} from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import useNavigator from "@saleor/hooks/useNavigator";
import { formatThousandDecimal } from "@saleor/utils/number";
import printJS from "print-js";
import React, { FC } from "react";
import InlineSVG from "react-inlinesvg";
import { FormattedMessage, useIntl } from "react-intl";

import useStyles from "./styles";
export interface CreateOrderFooterProps {
  orderId: string;
  params: PreviewOrderQueryParam;
  onDownloadPdf: MutationFunction<
    DownloadPdfOrderMutation,
    DownloadPdfOrderMutationVariables
  >;
  onPrintChangeOrder: MutationFunction<
    PrintChangeOrderMutation,
    PrintChangeOrderMutationVariables
  >;
  previewOrder?:
    | PreviewCustomerOrderQuery["previewCustomerOrder"]
    | PreviewDomesticPageOrderQuery["previewDomesticPageOrder"];
}

const PreviewOrderFooter: FC<CreateOrderFooterProps> = ({
  orderId,
  onDownloadPdf,
  onPrintChangeOrder,
  previewOrder,
  params
}) => {
  const [appState, _] = useAppState();
  const navigate = useNavigator();
  const classes = useStyles({
    expandMenu: appState.expandMenu,
    bodyPaddingRight: appState.bodyPaddingRight
  });
  const intl = useIntl();
  const permissions = useUserPermissions();
  const extensionsPermissionDomestic = permissions?.find(
    perm => perm.code === PermissionEnum.VIEW_ORDER_DOMESTIC
  );

  const handleDownloadFile = async () => {
    let base64;

    if (extensionsPermissionDomestic) {
      const { data } = await onPrintChangeOrder({
        variables: {
          orderId,
          sortType: getPreviewOrderFilterTable(params).direction
        }
      });
      base64 = data?.printChangeOrder?.exportedFileBase64;
    } else {
      const { data } = await onDownloadPdf({
        variables: {
          orderId,
          sortType: getPreviewOrderFilterTable(params).direction
        }
      });
      base64 = data?.downloadPdfOrder?.exportedFileBase64;
    }
    printJS({
      printable: base64,
      type: "pdf",
      base64: true
    });
  };

  return (
    <div className={classes.menu}>
      <Paper className={classes.wrapper}>
        <div className={classes.section}>
          <div>
            <MyTypography
              variant="subtitle2"
              className={classes.summaryCaption}
            >
              {intl.formatMessage({
                defaultMessage: "สรุปยอดคำสั่งซื้อ"
              })}
            </MyTypography>
            <MyTypography variant="body1" className={classes.number}>
              ({previewOrder?.previewOrderLines?.totalCount}{" "}
              {intl.formatMessage({
                defaultMessage: "items"
              })}
              )
            </MyTypography>
          </div>
          <div>
            <div className={classes.price}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {intl.formatMessage({
                  defaultMessage: "ยอดรวมสุทธิ"
                })}
              </MyTypography>
              <MyTypography variant="body1" className={classes.number}>
                {previewOrder?.totalPrice ? (
                  <DecimalFormat
                    value={previewOrder?.totalPrice}
                    isGrayDecimal={false}
                    countDecimal={2}
                  />
                ) : (
                  "0.00"
                )}
              </MyTypography>
            </div>
            <div className={classes.price}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {intl.formatMessage({
                  defaultMessage: "ภาษีมูลค่าเพิ่ม 7%"
                })}
              </MyTypography>
              <MyTypography variant="body1" className={classes.number}>
                {previewOrder?.taxAmount ? (
                  <DecimalFormat
                    value={previewOrder?.taxAmount}
                    isGrayDecimal={false}
                    countDecimal={2}
                  />
                ) : (
                  "0.00"
                )}
              </MyTypography>
            </div>
            <div className={classes.price}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {intl.formatMessage({
                  defaultMessage: "ยอดรวมภาษีมูลค่าเพิ่ม"
                })}
              </MyTypography>
              <MyTypography variant="subtitle1" className={classes.number}>
                <span className={classes.netPriceNumber}>
                  {previewOrder?.totalPriceIncTax
                    ? formatThousandDecimal(previewOrder?.totalPriceIncTax, 2)
                    : "0.00"}
                </span>
              </MyTypography>
            </div>
            <div className={classes.priceCurrency}>
              <MyTypography variant="caption" className={classes.priceCaption}>
                {intl.formatMessage({
                  defaultMessage: "Currency"
                })}
              </MyTypography>
              <MyTypography variant="body1" className={classes.currency}>
                {previewOrder?.__typename === "PreviewOrderLines"
                  ? previewOrder?.currency?.code
                  : previewOrder?.currency}
              </MyTypography>
            </div>
          </div>
        </div>
        <hr className={classes.divider} />
        <div className={classes.sectionBtn}>
          <div className={classes.sectionBtnLeft}>
            <Button
              id="backButton"
              data-testid="backButton"
              variant="contained"
              color="secondary"
              onClick={() => navigate("/")}
              className={classes.btnConfirmOrder}
              startIcon={<InlineSVG src={backIcon} />}
            >
              <MyTypography variant="body1">
                <FormattedMessage defaultMessage="กลับหน้าหลัก" />
              </MyTypography>
            </Button>
            <Button
              id="printButton"
              data-testid="printButton"
              variant="outlined"
              color="secondary"
              className={classes.btnAddProduct}
              onClick={handleDownloadFile}
              startIcon={<InlineSVG src={printIcon} />}
            >
              <MyTypography variant="body1">
                <FormattedMessage defaultMessage="พิมพ์เอกสาร" />
              </MyTypography>
            </Button>
            <a id="download" download>
              &nbsp;
            </a>
          </div>
          {extensionsPermissionDomestic && (
            <Button
              id="changeOrderButton"
              data-testid="changeOrderButton"
              variant="contained"
              color="secondary"
              onClick={() =>
                previewOrder?.soNo
                  ? navigate(
                      `${changeOrderPath(previewOrder.soNo)}?editMode=EDIT`
                    )
                  : navigate("/")
              }
              className={classes.btnChangeOrder}
              startIcon={<InlineSVG src={switchHorizontalIcon} />}
            >
              <FormattedMessage defaultMessage="Change Order" />
            </Button>
          )}
        </div>
      </Paper>
    </div>
  );
};

export default PreviewOrderFooter;
